<template>
    <div class="mainpage">
        <Dialog v-model:visible="dialogVisible" modal header="Are you sure you want to proceed?" :style="{ width: '40rem' }">
            <div class="flex justify-content-between">
                <Button  type="button" label="Update" @click="confirm(true)"></Button>
                <Button class="mx-2" type="button" label="Cancel" severity="secondary" @click="confirm(false)"></Button>
            </div>
        </Dialog>
        <div class="d-flex justify-content-between mb-2">
            <div class="d-flex">
                <div class="gap">
                    <span class="p-input-icon-left">
                        <i class="pi pi-search" />
                        <InputText v-model="searchValue" placeholder="Search by CTA"/>
                    </span>
                </div>
                <Dropdown class="gap" v-model="ctaType" :options="filters" optionLabel="label" placeholder="Filter by CTA type" optionValue="value" />
            </div>
            <div class="d-flex justify-content-end">
                <Button icon="pi pi-download" class="gap" label="Download All" @click="downloadAllCtas" :disabled="downloadInProgress"/>
                <Calendar ref="searchCalendar" @date-select="dateSearch" class="gap" v-model="date" showIcon  selectionMode="range" :manualInput="false" placeholder="Last 30 days" :maxDate="maxDate"/>
                <Button icon="pi pi-plus" label="Whitelist CTA" @click="whitelist"/>
            </div>
        </div>
        <div class="custom-table">
            <DataTable class="p-datatable" :value="filteredItems" tableStyle="min-width: 50rem"
                paginator :rows="10" :rowsPerPageOptions="[10, 20, 50]"
                paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            >
                <template #empty>
                    <div class="empty-state"><img class="empty-state-image" src="@/assets/images/empty-state.svg"/></div>
                    <div class="empty-state">No data available</div>
                </template>
                <Column class="dltctaid-column" field="DltCtaID" header="CTA ID" style="min-width: 100px;">
                    <template #body="slotProps">
                        {{ slotProps.data.DltCtaID }}
                    </template>
                </Column>
                <Column field="Type" header="Type" style="min-width: 100px;">
                    <template #body="slotProps">
                        {{ ctaTypeToNameMap[slotProps.data.Type] }}
                    </template>
                </Column>
                <Column field="SubType" header="Sub Type" style="min-width: 120px;">
                    <template #body="slotProps">
                        {{ subTypeToNameMap[slotProps.data.SubType] }}
                    </template>
                </Column>
                <Column class="overflow-column" field="Cta" header="CTA" style="max-width: 20vw"></Column>
                <Column class="overflow-column-disable" field="SampleCTA" header="Sample CTA" style="max-width: 20vw">
                    <template #body="slotProps">
                        {{ slotProps.data.SampleCTA ? slotProps.data.SampleCTA : 'N/A' }}
                    </template>
                </Column>
                <Column field="HasVariable" header="Dynamic" style="min-width: 100px;">
                    <template #body="slotProps">
                        {{ slotProps.data.SubType === 'sd' ? 'Yes' : (slotProps.data.HasVariable ? 'Yes' : 'No') }}
                    </template>
                </Column>
                <Column field="RegDLT" header="Regd Telco" style="min-width: 100px;"/>
                <Column field="LastUpdated" header="Updated On" style="min-width: 100px;">
                    <template #body="slotProps">
                        {{ slotProps.data.LastUpdated }}
                    </template>
                </Column>
                <Column field="Status" header="Status" style="min-width: 100px;">
                    <template #body="slotProps">
                        {{ getStatus(slotProps.data.Status) }}
                    </template>
                </Column>
                <Column header="Action" style="min-width: 100px;">
                    <template #body="slotProps">
                        <InputSwitch
                        :disabled="disabledIDs.includes(slotProps.data.CtaID) || disabledCodes.includes(slotProps.data.Status) || slotProps.data.RegDLT !== registeredDlt"
                        :modelValue="slotProps.data.Status" @update:model-value="confirmCtaUpdate($event, slotProps.data)"
                        :true-value="1"
                        :false-value="-1"/>
                    </template>
                </Column>
            </DataTable>
            <!-- <Paginator :rows="10" :totalRecords="120" :rowsPerPageOptions="[10, 20, 30]"></Paginator> -->
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapModels } from '@/utils'
import { ctaTypeToNameMap, whitelistStatusMap, subTypeToNameMap } from '@/constants/index'
import moment from 'moment'
import InputSwitch from 'primevue/inputswitch'
import config from '@/config/config'
import Toastr from 'toastr'

export default {
    name: 'Whitelisted CTA',
    components: {
        InputSwitch
    },
    data () {
        return {
            dialogVisible: false,
            maxDate: null,
            searchValue: '',
            ctaType: '',
            filters: [ { label: 'Email', value: 'email' }, { label: 'Mobile', value: 'mobile' }, { label: 'URL', value: 'url' } ],
            date: [],
            startDay: '',
            endDay: '',
            ctaTypeToNameMap,
            whitelistStatusMap,
            subTypeToNameMap,
            registeredDlt: config.REGISTERED_DLT,
            downloadInProgress: false,
            disabledIDs: [],
            disabledCodes: [0, 4, 5, -100],
            isConfirmed: false,
            pendingCtaData: null,
            pendingCtaStatus: null
        }
    },
    methods: {
        getStatus (status) {
            switch (status) {
            case 1:
                return 'ACTIVE'
            case 0:
                return 'PENDING'
            case -1:
                return 'SUSPENDED'
            case 4:
                return 'REJECTED'
            case 5:
                return 'BLACKLISTED'
            case -100:
                return 'INVALID'
            default:
                return 'ERROR'
            }
        },
        whitelist () {
            this.$router.push('/cta/whitelist')
        },
        async dateSearch () {
            if (this.date[1] != null) {
                this.$refs.searchCalendar.overlayVisible = false
                await this.getWhitelistedCtas()
            }
        },
        async getWhitelistedCtas () {
            this.startDay = ''
            this.endDay = ''
            if (!((this.date[0] == null) ^ (this.date[1] == null))) {
                if (this.date[0] && this.date[1]) {
                    this.startDay = moment(this.date[0])
                    this.endDay = moment(this.date[1]).endOf('day')
                }
            } else {
                this.date = []
            }
            const data = {
                from: this.startDay,
                to: this.endDay,
                type: this.ctaType
            }
            this.globalLoader = true
            await this.$store.dispatch('Cta/Whitelist/fetchCtas', data)
            this.globalLoader = false
        },
        async confirmCtaUpdate (newStatus, data) {
            this.pendingCtaStatus = newStatus
            this.pendingCtaData = data
            this.dialogVisible = true
        },
        async confirm (isConfirmed) {
            try {
                if (isConfirmed) {
                    const data = this.pendingCtaData
                    data.Status = this.pendingCtaStatus
                    this.disabledIDs.push(data.CtaID)
                    const payload = {
                        id: data.CtaID,
                        type: data.Type,
                        sub_type: data.SubType,
                        cta: data.Cta,
                        sample_cta: data.SampleCTA,
                        has_variables: data.HasVariable,
                        not_shorten: data.Shorten,
                        status: parseInt(data.Status),
                        dltCtaID: data.DltCtaID
                    }
                    this.$store.dispatch('Cta/Whitelist/whitelist', payload)
                }
            } catch (error) {
                Toastr.error('Error occured while update')
            } finally {
                this.pendingCtaData = null
                this.pendingCtaStatus = null
                this.dialogVisible = false
            }
        },
        async downloadAllCtas () {
            this.downloadInProgress = true
            try {
                this.startDay = ''
                this.endDay = ''
                if (!((this.date[0] == null) ^ (this.date[1] == null))) {
                    if (this.date[0] && this.date[1]) {
                        this.startDay = moment(this.date[0])
                        this.endDay = moment(this.date[1]).endOf('day')
                    }
                } else {
                    this.date = []
                }
                const data = {
                    from: this.startDay,
                    to: this.endDay,
                    type: this.ctaType,
                    search: this.searchValue
                }
                let response = await this.$store.dispatch('Cta/Whitelist/downloadAllCtas', data)
                if (response && response.status === 200 && response.data) {
                    const contentDisposition = response.headers['content-disposition']
                    let filename = 'data.csv'
                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
                        if (filenameMatch && filenameMatch.length === 2) {
                            filename = filenameMatch[1]
                        }
                    }

                    const blob = new Blob([response.data], { type: '.csv' })
                    const link = document.createElement('a')
                    link.href = URL.createObjectURL(blob)
                    link.download = filename
                    link.click()
                    URL.revokeObjectURL(link.href)
                } else {
                    Toastr.error('File not found')
                }
            } catch (error) {
                console.error(error)
                Toastr.error('Error occured while downloading file')
            }
            this.downloadInProgress = false
        }
    },
    computed: {
        ...mapGetters({ currentUser: 'currentUser' }),
        ...mapModels('general', ['globalLoader']),
        ...mapGetters('Cta/Whitelist', ['getTableData']),
        filteredItems: function () {
            return this.getTableData.filter(item => {
                if (this.searchValue && this.ctaType) {
                    return (item.Cta.toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1) && (item.Type === this.ctaType)
                }
                if (this.searchValue) {
                    return item.Cta.toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1
                }
                if (this.ctaType) {
                    return item.Type === this.ctaType
                }
                return item
            })
        }
    },
    async mounted () {
        this.disabledIDs = []
        this.maxDate = new Date()
        await this.getWhitelistedCtas()
    }
}
</script>

<style>
.gap {
    margin-right: 0.5rem;
}
.overflow-column {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.overflow-column:hover {
    text-overflow: clip;
    white-space: normal;
    word-break: break-all;
}
.overflow-column-disable {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.dltctaid-column:hover {
    text-decoration: underline;
    cursor: pointer;
}
</style>
